import Keycloak from 'keycloak-js';
import { http, initHttpClient, SpocData } from './js/api/axios';
import { log } from './js/shared/utils/log';
import isNil from 'lodash/isNil';
import Utils, { useIsAdmin } from './js/shared/utils';
import { appConfig } from './js/config';
import { useLayoutEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import isEqual from 'lodash/isEqual';
import find from 'lodash/find';
import {
    initCookie,
    loadSpocCookie,
    logout,
    saveTutorialCookie,
    TutorialCookie
} from './js/app-store/authentication/auth-utils';
import { SpocState } from './js/app-store/authentication/authenticationReducer';
import { useAuthentication } from './js/app-store/authentication/useAuthentication';
import { useTogglz } from './js/app-store/togglz/useTogglz';
import { Toggl } from './js/shared/utils/togglz';
import { useSpocs } from './js/api/spocs/useSpocs';
import * as Sentry from '@sentry/react';

export const SPOC_DATA: SpocData = { spocId: '' };

const kc = new Keycloak('/keycloak.json');

export const useKeycloak = () => {
    return kc;
};

function useKeycloakAndInit() {
    const [initState, setInitState] = useState<{ maintenance: boolean; isReady: boolean }>({
        maintenance: false,
        isReady: false
    });

    const [authenticated, setAuthenticated] = useState(false);
    const isAdmin = useIsAdmin();
    const { data: spocs, isError: isSpocsError } = useSpocs(authenticated);

    useLayoutEffect(() => {
        initHttpClient(kc, SPOC_DATA);
    }, []);

    useLayoutEffect(() => {
        if (isSpocsError) {
            setInitState({ isReady: true, maintenance: true });
        }
    }, [isSpocsError]);

    const { setSpoc, spoc } = useAuthentication(authenticated);

    const { setTogglz } = useTogglz();

    useLayoutEffect(() => {
        // ************************************
        // keycloak initialization
        // ************************************
        const token = localStorage.getItem('token') ?? undefined;
        const refreshToken = localStorage.getItem('refreshToken') ?? undefined;

        kc.init({ onLoad: 'check-sso', checkLoginIframe: false, token, refreshToken })
            .then(authenticated => {
                if (authenticated && kc.token && kc.refreshToken) {
                    localStorage.setItem('token', kc.token);
                    localStorage.setItem('refreshToken', kc.refreshToken);

                    setInterval(() => {
                        kc.updateToken(300)
                            .then(refreshed => {
                                if (refreshed && kc.token && kc.refreshToken) {
                                    localStorage.setItem('token', kc.token);
                                    localStorage.setItem('refreshToken', kc.refreshToken);
                                }
                            })
                            .catch(error => {
                                log.error(error ?? 'Unable to refresh token');
                                logout(kc);
                            });
                    }, 30000);

                    initCookie();

                    if (!isNil(kc) && !isNil(kc.tokenParsed)) {
                        const tokenParsed = kc.tokenParsed;
                        if (
                            tokenParsed &&
                            isNil(tokenParsed.partnerId) &&
                            isNil(tokenParsed.siteIds) &&
                            !Utils.isUserAdmin(kc)
                        ) {
                            const msg =
                                'No context found for your user\n\n' +
                                'Please inform your system administrator about this problem!';
                            logout(kc);
                            alert(msg);
                            return;
                        }
                    }
                    setAuthenticated(true);
                    Sentry.setUser({
                        email: kc.tokenParsed?.email,
                        username: kc.tokenParsed?.preferred_username
                    });
                } else {
                    // show possibly some other page here...
                    kc.login();
                }
            })
            .catch(error => log.error(error ?? 'authentication failed'));
    }, []);

    useLayoutEffect(() => {
        if (!spoc.id && spocs && spocs.length > 0) {
            selectSpoc({ spocs, setSpoc });
            initServices(isAdmin).then();
        }
    }, [setSpoc, isAdmin, spocs, spoc.id]);

    useLayoutEffect(() => {
        if (authenticated) {
            http.get<TutorialCookie>(appConfig().endpointTutorial)
                .then(({ data }) => {
                    saveTutorialCookie(data);
                })
                .catch(_ => {
                    setInitState({ isReady: true, maintenance: true });
                });
        }
    }, [authenticated]);

    useLayoutEffect(() => {
        if (spoc.id) {
            http.get<Toggl[]>('/manage/togglz').then(res => {
                setTogglz(res.data);
                setInitState({ isReady: true, maintenance: false });
            });
        }
    }, [spoc.id, setTogglz]);

    return initState;
}

const selectSpoc = ({ spocs, setSpoc }: { spocs: Array<SpocState>; setSpoc: (spoc: SpocState) => void }) => {
    const restoredSpoc = loadSpocCookie();
    const spocID: string | null = restoredSpoc ? restoredSpoc.id : null;
    let futureSpoc: SpocState | undefined = undefined;
    let allowed = false;
    if (spocID && spocs) {
        spocs.some(partner => {
            if (isEqual(spocID, partner.id)) {
                allowed = true;
                futureSpoc = partner;
                return true;
            }

            if (partner.sites) {
                partner.sites.some(site => {
                    if (isEqual(spocID, site.id)) {
                        allowed = true;
                        futureSpoc = site;
                        return true;
                    }

                    return false;
                });
                if (!isNil(futureSpoc)) return true;
            }

            return false;
        });
    }

    if (!futureSpoc || !allowed) {
        const token = kc.tokenParsed;
        if (token && token.partnerId) {
            futureSpoc = find(spocs, {
                id: `PARTNER${token.partnerId}`
            });
        }
        if (!futureSpoc) {
            futureSpoc = spocs[0];
        }
        if (futureSpoc.sites && futureSpoc.sites[0]) {
            futureSpoc = futureSpoc.sites[0];
        }
    }

    setSpoc(futureSpoc);
};

// ************************************
// external stage configuration
// ************************************

const initServices = (isAdminUser: boolean) =>
    http
        .get('/config.json', { external: true })
        .then(response => {
            const config = response.data;
            ReactGA.initialize(config.gaMeasurementID);
            ReactGA.ga('set', 'dimension1', isAdminUser ? 'internal' : 'external');
            ReactGA.ga('send', 'pageview', { dimension1: isAdminUser ? 'internal' : 'external' });
        })
        .catch(err => {
            throw new Error(`Could not initialize services. Error: ${err.message}`);
        });

export default useKeycloakAndInit;
